import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Group, Image as KonvaImage, Layer, Line, Rect, Stage, Text } from "react-konva";
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignHorizontalDistributeCenter,
  AlignHorizontalJustifyEnd,
  AlignHorizontalJustifyStart,
  AlignVerticalDistributeCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  Copy,
  Hand,
  LayoutGrid,
  Maximize2,
  MousePointer2,
  Rows2,
  Save,
  SendHorizontal,
  Undo2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import toast from "react-hot-toast";
import useImage from "use-image";

import { API } from "../../../api";
import ImageUploader from "../../../Components/Common/ImageUploader";
import {
  type SeatLayoutBackgroundUploadResponse,
  type SeatLayoutDetail,
  seatLayoutService,
} from "../../../api/services/seat-layout.service";
import { dialogService, normalizeMessage } from "../../../utils/dialogService";
import { createClientKey, createDraftFingerprint, stripDraft } from "../core/draft";
import { getSeatBounds, normalizeSelectionRect } from "../core/geometry";
import {
  SEAT_BUTTON_SHAPE_OPTIONS,
  type SeatButtonShape,
  getSeatButtonShape,
  getSeatShapeGeometry,
  getSeatShapeTextOffset,
  withSeatButtonShapeMeta,
} from "../core/seatShapes";
import type { LayoutStudioDraft } from "../core/types";
import { useLayoutCanvasEditor } from "../core/useLayoutCanvasEditor";
import { useLayoutSyncNotice } from "../core/useLayoutSyncNotice";
import { buildInitialDraft, findTierColor, normalizeReservationPackage } from "./adapter";
import type { ReservationPackageResponse } from "./types";

type UploadedBackgroundAsset = Partial<SeatLayoutBackgroundUploadResponse> & {
  id?: number;
  url?: string;
  width?: number;
  height?: number;
  size?: number;
  is_wide?: boolean;
};

const TOOLBAR_ICON_BUTTON_BASE_CLASS =
  "inline-flex h-10 w-10 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-40";
const TOOLBAR_ICON_BUTTON_INACTIVE_CLASS = "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:text-slate-900";
const TOOLBAR_ICON_BUTTON_ACTIVE_CLASS = "border-slate-900 bg-slate-900 text-slate-50 shadow-sm hover:border-slate-900 hover:bg-slate-800 hover:text-white";

const ToolbarDivider = () => <div className="mx-1 h-8 w-px shrink-0 bg-slate-200" />;

type ToolbarIconButtonProps = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
};

const ToolbarIconButton: React.FC<ToolbarIconButtonProps> = ({ icon: Icon, label, onClick, active, disabled }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    className={`${TOOLBAR_ICON_BUTTON_BASE_CLASS} ${active ? TOOLBAR_ICON_BUTTON_ACTIVE_CLASS : TOOLBAR_ICON_BUTTON_INACTIVE_CLASS}`}
    onClick={onClick}
    disabled={disabled}
  >
    <Icon className="h-4 w-4" />
  </button>
);

const formatPersistedAt = (value: string | null) => {
  if (!value) {
    return "아직 저장되지 않음";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "방금 저장됨";
  }

  return parsed.toLocaleString("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getSeatLayoutErrorMessage = (error: unknown, fallback: string) => normalizeMessage(error) || fallback;

const BackgroundImage: React.FC<{ backgroundImageUrl: string; width: number; height: number }> = ({
  backgroundImageUrl,
  width,
  height,
}) => {
  const [backgroundImage] = useImage(backgroundImageUrl, "anonymous");

  if (!backgroundImage) {
    return null;
  }

  return <KonvaImage image={backgroundImage} x={0} y={0} width={width} height={height} opacity={0.92} listening={false} />;
};

const SeatLayoutStudio: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedPackageId, setSelectedPackageId] = React.useState<number | null>(null);
  const [draft, setDraft] = React.useState<LayoutStudioDraft | null>(null);
  const [selectedTierIndex, setSelectedTierIndex] = React.useState(0);
  const [savedFingerprint, setSavedFingerprint] = React.useState("");
  const [lastPersistedAt, setLastPersistedAt] = React.useState<string | null>(null);
  const [backgroundUploadRevision, setBackgroundUploadRevision] = React.useState(0);

  const packagesQuery = useQuery({
    queryKey: ["seat-layout-packages"],
    queryFn: async () => {
      const response = await API.get("/package/items/?include_hidden=true");
      return (response.data as ReservationPackageResponse[])
        .map(normalizeReservationPackage)
        .filter((item) => item.has_spots && item.spots.length > 0);
    },
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (!selectedPackageId && packagesQuery.data?.length) {
      setSelectedPackageId(packagesQuery.data[0].id);
    }
  }, [packagesQuery.data, selectedPackageId]);

  const selectedPackage = React.useMemo(
    () => packagesQuery.data?.find((item) => item.id === selectedPackageId) || null,
    [packagesQuery.data, selectedPackageId],
  );

  const layoutQuery = useQuery({
    queryKey: ["seat-layout-detail", selectedPackageId],
    queryFn: async () => {
      if (!selectedPackageId) {
        return null;
      }
      const listResponse = await seatLayoutService.list({ reservation_package: selectedPackageId, page_size: 50 });
      const layoutSummary = listResponse.data.results[0];
      if (!layoutSummary) {
        return null;
      }
      const detailResponse = await seatLayoutService.get(layoutSummary.id);
      return detailResponse.data;
    },
    enabled: Boolean(selectedPackageId),
    refetchOnWindowFocus: false,
  });

  const currentFingerprint = React.useMemo(() => createDraftFingerprint(draft), [draft]);
  const isDirty = Boolean(draft) && currentFingerprint !== savedFingerprint;

  const {
    selectedSeatIds,
    setSelectedSeatIds,
    selectedSeatIdsSet,
    selectedSeats,
    selectionRect,
    canvasViewportRef,
    stageRef,
    activeTool,
    setActiveTool,
    zoomLevel,
    canvasScale,
    canvasOrigin,
    stageWidth,
    stageHeight,
    canvasCursor,
    updateSeat,
    updateSeats,
    alignSelectedSeats,
    distributeSelectedSeats,
    setZoomAtPoint,
    resetCanvasView,
    resetEditorState,
    handleSeatSelect,
    handleStageMouseDown,
    handleStageMouseMove,
    handleStageMouseUp,
    handleStageWheel,
    getRenderedSeatPosition,
    handleSeatPointerDown,
    handleSeatDragStart,
    handleSeatDragMove,
    handleSeatDragEnd,
    handleSeatDragCancel,
  } = useLayoutCanvasEditor({ draft, setDraft });

  React.useEffect(() => {
    if (!selectedPackage) {
      setDraft(null);
      setSavedFingerprint("");
      setLastPersistedAt(null);
      resetEditorState();
      return;
    }
    const nextDraft = buildInitialDraft(selectedPackage, layoutQuery.data);
    setDraft(nextDraft);
    setSavedFingerprint(createDraftFingerprint(nextDraft));
    setLastPersistedAt(layoutQuery.data?.updated_at || null);
    setSelectedTierIndex(0);
    resetEditorState();
    setBackgroundUploadRevision((value) => value + 1);
  }, [layoutQuery.data, resetEditorState, selectedPackage]);

  React.useEffect(() => {
    if (!isDirty) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const handleReloadLatest = React.useCallback(async () => {
    await layoutQuery.refetch();
  }, [layoutQuery]);

  const { notice, dismissNotice, broadcast } = useLayoutSyncNotice({
    reservationPackageId: selectedPackageId,
    layoutId: draft?.id ?? layoutQuery.data?.id ?? null,
    isDirty,
    onReloadLatest: handleReloadLatest,
  });

  const dirtyBroadcastedRef = React.useRef(false);

  React.useEffect(() => {
    if (!draft || !selectedPackageId) {
      dirtyBroadcastedRef.current = false;
      return;
    }

    if (isDirty && !dirtyBroadcastedRef.current) {
      broadcast({ eventType: "draft-dirty" });
      dirtyBroadcastedRef.current = true;
      return;
    }

    if (!isDirty) {
      dirtyBroadcastedRef.current = false;
    }
  }, [broadcast, draft, isDirty, selectedPackageId]);

  const applyPersistedLayout = React.useCallback(
    (data: SeatLayoutDetail) => {
      if (!selectedPackage) {
        return;
      }

      const nextDraft = buildInitialDraft(selectedPackage, data);
      setDraft(nextDraft);
      setSavedFingerprint(createDraftFingerprint(nextDraft));
      setLastPersistedAt(data.updated_at);
      setSelectedTierIndex(0);
      resetEditorState();
      dismissNotice();
      queryClient.invalidateQueries({ queryKey: ["seat-layout-detail", selectedPackageId] });
      setBackgroundUploadRevision((value) => value + 1);
    },
    [dismissNotice, queryClient, resetEditorState, selectedPackage, selectedPackageId],
  );

  const saveMutation = useMutation({
    mutationFn: async (currentDraft: LayoutStudioDraft) => {
      const payload = stripDraft(currentDraft);
      if (currentDraft.id) {
        return (await seatLayoutService.update(currentDraft.id, payload)).data;
      }
      return (await seatLayoutService.create(payload)).data;
    },
    onSuccess: (data) => {
      applyPersistedLayout(data);
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (layoutId: number) => (await seatLayoutService.publish(layoutId)).data,
    onSuccess: (data) => {
      applyPersistedLayout(data);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (layoutId: number) => (await seatLayoutService.duplicate(layoutId)).data,
    onSuccess: (data) => {
      applyPersistedLayout(data);
      toast.success("좌석도를 복제했습니다.");
    },
    onError: (error: unknown) => {
      toast.error(getSeatLayoutErrorMessage(error, "좌석도 복제에 실패했습니다."));
    },
  });

  const selectedSeatId = selectedSeatIds.length === 1 ? selectedSeatIds[0] : null;
  const selectedSeat = selectedSeatId ? selectedSeats.find((seat) => seat.spot === selectedSeatId) || null : null;

  const backgroundUploadValue = React.useMemo(() => {
    if (!draft?.background_image_url) {
      return null;
    }

    return {
      id: backgroundUploadRevision,
      url: draft.background_image_url,
      width: draft.canvas_width,
      height: draft.canvas_height,
      size: 0,
      is_wide: draft.canvas_width >= draft.canvas_height,
    };
  }, [backgroundUploadRevision, draft]);

  const addTier = () => {
    setDraft((prev) => {
      if (!prev) {
        return prev;
      }
      const next = {
        ...prev,
        packages: [
          ...prev.packages,
          {
            client_key: createClientKey("tier"),
            name: `존 ${prev.packages.length + 1}`,
            color: "#2563EB",
            display_order: prev.packages.length + 1,
          },
        ],
      };
      setSelectedTierIndex(next.packages.length - 1);
      return next;
    });
  };

  const removeTier = (tierIndex: number) => {
    setDraft((prev) => {
      if (!prev || prev.packages.length === 1) {
        return prev;
      }
      const removed = prev.packages[tierIndex];
      const fallback = prev.packages[tierIndex === 0 ? 1 : 0];
      return {
        ...prev,
        packages: prev.packages.filter((_, index) => index !== tierIndex),
        seats: prev.seats.map((seat) => {
          if (seat.layout_package === removed.id || seat.layout_package_client_key === removed.client_key) {
            return {
              ...seat,
              layout_package: fallback.id,
              layout_package_client_key: fallback.id ? undefined : fallback.client_key,
            };
          }
          return seat;
        }),
      };
    });
    setSelectedTierIndex(0);
  };

  const autoArrange = () => {
    setDraft((prev) => {
      if (!prev) {
        return prev;
      }
      const columns = Math.max(4, Math.floor((prev.canvas_width - 120) / 88));
      return {
        ...prev,
        seats: prev.seats.map((seat, index) => ({
          ...seat,
          x: 64 + (index % columns) * 88,
          y: 72 + Math.floor(index / columns) * 88,
          display_order: index + 1,
        })),
      };
    });
  };

  const resetDraft = () => {
    if (!selectedPackage) {
      return;
    }
    const nextDraft = buildInitialDraft(selectedPackage, layoutQuery.data);
    setDraft(nextDraft);
    setSavedFingerprint(createDraftFingerprint(nextDraft));
    setLastPersistedAt(layoutQuery.data?.updated_at || null);
    setSelectedTierIndex(0);
    resetEditorState();
    dismissNotice();
    setBackgroundUploadRevision((value) => value + 1);
    toast.success("마지막 저장 상태로 되돌렸습니다.");
  };

  const handlePackageChange = async (nextPackageId: number) => {
    if (nextPackageId === selectedPackageId) {
      return;
    }

    if (isDirty) {
      const confirmed = await dialogService.confirm({
        title: "저장되지 않은 변경사항",
        description: "저장하지 않은 좌석도 변경사항이 있습니다. 다른 상품으로 이동하면 현재 수정 내용이 사라집니다. 계속하시겠습니까?",
        confirmLabel: "이동",
        cancelLabel: "취소",
        tone: "warning",
      });

      if (!confirmed) {
        return;
      }
    }

    dismissNotice();
    setSelectedPackageId(nextPackageId);
  };

  const handleSave = async () => {
    if (!draft) {
      return;
    }

    try {
      await saveMutation.mutateAsync(draft);
      broadcast({ eventType: "draft-saved" });
      toast.success("좌석도 초안을 저장했습니다.");
    } catch (error) {
      toast.error(getSeatLayoutErrorMessage(error, "좌석도 저장에 실패했습니다."));
    }
  };

  const handlePublish = async () => {
    if (!draft) {
      return;
    }

    try {
      let layoutId = draft.id;

      if (!layoutId) {
        const savedLayout = await saveMutation.mutateAsync(draft);
        layoutId = savedLayout.id;
      }

      if (!layoutId) {
        throw new Error("저장된 좌석도 ID를 확인할 수 없습니다.");
      }

      await publishMutation.mutateAsync(layoutId);
      broadcast({ eventType: "draft-published" });
      toast.success("좌석도를 발행했습니다.");
    } catch (error) {
      toast.error(
        getSeatLayoutErrorMessage(
          error,
          draft.id ? "좌석도 발행에 실패했습니다." : "좌석도 저장 또는 발행에 실패했습니다.",
        ),
      );
    }
  };

  const handleBackgroundChange = (asset: UploadedBackgroundAsset | null) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            background_image_url: asset?.asset_url || asset?.url || "",
          }
        : prev,
    );
    setBackgroundUploadRevision((value) => value + 1);
  };

  const applySelectedTier = (value: string) => {
    if (!draft || !selectedSeatIds.length) {
      return;
    }

    const matchedTier = draft.packages.find((item) => String(item.id || item.client_key) === value);
    if (!matchedTier) {
      return;
    }

    updateSeats(selectedSeatIds, {
      layout_package: matchedTier.id,
      layout_package_client_key: matchedTier.id ? undefined : matchedTier.client_key,
    });
  };

  const applySelectedShape = (value: string) => {
    if (!draft || !selectedSeatIds.length) {
      return;
    }

    const matchedShape = SEAT_BUTTON_SHAPE_OPTIONS.find((option) => option.value === value)?.value;
    if (!matchedShape) {
      return;
    }

    const targetIds = new Set(selectedSeatIds);
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            seats: prev.seats.map((seat) =>
              targetIds.has(seat.spot)
                ? {
                    ...seat,
                    meta: withSeatButtonShapeMeta(seat.meta, matchedShape),
                  }
                : seat,
            ),
          }
        : prev,
    );
  };

  const allSelectedSeatsActive = selectedSeats.length > 0 && selectedSeats.every((seat) => seat.is_active);
  const selectedSeatBounds = getSeatBounds(selectedSeats);

  if (packagesQuery.isLoading) {
    return <div className="p-6">좌석도 설정을 불러오는 중입니다.</div>;
  }

  if (!packagesQuery.data?.length) {
    return <div className="p-6">좌석 기반 예약 상품이 없습니다.</div>;
  }

  return (
    <div className="flex h-[calc(100vh-120px)] max-h-[calc(100vh-120px)] min-h-0 w-full flex-col gap-4 overflow-hidden p-4">
      {draft ? (
        <div className="flex shrink-0 items-center justify-between gap-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex items-center gap-2">
            <ToolbarIconButton icon={MousePointer2} label="선택 모드" onClick={() => setActiveTool("select")} active={activeTool === "select"} />
            <ToolbarIconButton icon={Hand} label="이동 모드" onClick={() => setActiveTool("pan")} active={activeTool === "pan"} />
            <ToolbarIconButton icon={ZoomIn} label="줌 모드" onClick={() => setActiveTool("zoom")} active={activeTool === "zoom"} />
            <ToolbarDivider />
            <ToolbarIconButton icon={LayoutGrid} label="전체 선택" onClick={() => setSelectedSeatIds(draft.seats.map((seat) => seat.spot))} />
            <ToolbarIconButton icon={X} label="선택 해제" onClick={() => setSelectedSeatIds([])} disabled={!selectedSeatIds.length} />
            <ToolbarDivider />
            <ToolbarIconButton icon={AlignHorizontalJustifyStart} label="왼쪽 정렬" onClick={() => alignSelectedSeats("left")} disabled={selectedSeatIds.length < 2} />
            <ToolbarIconButton icon={AlignCenterHorizontal} label="가로 중앙 정렬" onClick={() => alignSelectedSeats("h-center")} disabled={selectedSeatIds.length < 2} />
            <ToolbarIconButton icon={AlignHorizontalJustifyEnd} label="오른쪽 정렬" onClick={() => alignSelectedSeats("right")} disabled={selectedSeatIds.length < 2} />
            <ToolbarIconButton icon={AlignVerticalJustifyStart} label="위쪽 정렬" onClick={() => alignSelectedSeats("top")} disabled={selectedSeatIds.length < 2} />
            <ToolbarIconButton icon={AlignCenterVertical} label="세로 중앙 정렬" onClick={() => alignSelectedSeats("v-center")} disabled={selectedSeatIds.length < 2} />
            <ToolbarIconButton icon={AlignVerticalJustifyEnd} label="아래쪽 정렬" onClick={() => alignSelectedSeats("bottom")} disabled={selectedSeatIds.length < 2} />
            <ToolbarIconButton icon={AlignHorizontalDistributeCenter} label="가로 분배" onClick={() => distributeSelectedSeats("horizontal")} disabled={selectedSeatIds.length < 3} />
            <ToolbarIconButton icon={AlignVerticalDistributeCenter} label="세로 분배" onClick={() => distributeSelectedSeats("vertical")} disabled={selectedSeatIds.length < 3} />
            <ToolbarDivider />
            <ToolbarIconButton icon={Rows2} label="자동 정렬" onClick={autoArrange} />
            <ToolbarIconButton icon={Maximize2} label="화면 맞춤" onClick={resetCanvasView} />
            <ToolbarIconButton icon={Undo2} label="마지막 저장 상태로 되돌리기" onClick={resetDraft} disabled={!isDirty} />
          </div>

          <div className="flex items-center gap-2">
            <ToolbarIconButton icon={ZoomOut} label="축소" onClick={() => setZoomAtPoint(zoomLevel / 1.1)} />
            <div className="min-w-18 text-center text-sm font-semibold text-slate-700">{Math.round(zoomLevel * 100)}%</div>
            <ToolbarIconButton icon={ZoomIn} label="확대" onClick={() => setZoomAtPoint(zoomLevel * 1.1)} />
            <ToolbarDivider />
            {draft.id ? <ToolbarIconButton icon={Copy} label="복제" onClick={() => duplicateMutation.mutate(draft.id as number)} disabled={duplicateMutation.isPending} /> : null}
            <ToolbarIconButton icon={Save} label="초안 저장" onClick={() => void handleSave()} disabled={saveMutation.isPending} />
            <ToolbarIconButton icon={SendHorizontal} label="발행" onClick={() => void handlePublish()} disabled={publishMutation.isPending || saveMutation.isPending} />
          </div>
        </div>
      ) : null}

      {notice ? (
        <div
          className={`flex shrink-0 items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-sm ${
            notice.tone === "warning"
              ? "border-amber-300 bg-amber-50 text-amber-900"
              : "border-sky-300 bg-sky-50 text-sky-900"
          }`}
        >
          <span>{notice.message}</span>
          <div className="flex items-center gap-2">
            {notice.actionLabel && notice.onAction ? (
              <button
                type="button"
                className="rounded-lg border border-current px-3 py-1.5 text-sm font-semibold"
                onClick={() => void notice.onAction?.()}
              >
                {notice.actionLabel}
              </button>
            ) : null}
            <button type="button" className="rounded-lg px-2 py-1 text-xs font-semibold opacity-70" onClick={dismissNotice}>
              닫기
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
        <div className="flex h-full w-[320px] shrink-0 flex-col overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Seat Layout Studio</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">예약 좌석도</h2>
            <p className="mt-2 text-sm text-slate-500">상단 툴바에서 선택, 이동, 줌, 정렬, 저장 작업을 처리합니다.</p>
          </div>

          <label className="mb-2 block text-xs font-semibold text-slate-500">예약 상품</label>
          <select
            className="mb-4 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={selectedPackageId || ""}
            onChange={(event) => {
              void handlePackageChange(Number(event.target.value));
            }}
          >
            {packagesQuery.data.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>

          {draft ? (
            <>
              <label className="mb-2 block text-xs font-semibold text-slate-500">레이아웃 이름</label>
              <input
                className="mb-4 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />

              <div className="mb-4 grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-500">Canvas W</label>
                  <input
                    type="number"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    value={draft.canvas_width}
                    onChange={(event) => setDraft({ ...draft, canvas_width: Number(event.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-500">Canvas H</label>
                  <input
                    type="number"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    value={draft.canvas_height}
                    onChange={(event) => setDraft({ ...draft, canvas_height: Number(event.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="mb-4 space-y-3">
                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-500">배경 이미지 업로드</label>
                  <ImageUploader
                    value={backgroundUploadValue}
                    maxSizeMB={10}
                    accept={["image/jpeg", "image/png", "image/webp"]}
                    uploader={(file) => seatLayoutService.uploadBackgroundImage(file)}
                    onChange={handleBackgroundChange}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold text-slate-500">배경 이미지 URL</label>
                  <input
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    value={draft.background_image_url}
                    onChange={(event) => setDraft({ ...draft, background_image_url: event.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="mb-5 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                <div className="flex items-center justify-between">
                  <span>상태</span>
                  <span className="font-semibold text-slate-900">{draft.status || "draft"}</span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span>버전</span>
                  <span className="font-semibold text-slate-900">{draft.version || 0}</span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span>편집 상태</span>
                  <span className={`font-semibold ${isDirty ? "text-amber-600" : "text-emerald-600"}`}>
                    {saveMutation.isPending
                      ? "저장 중"
                      : publishMutation.isPending
                        ? "발행 중"
                        : duplicateMutation.isPending
                          ? "복제 중"
                          : isDirty
                            ? "저장되지 않은 변경사항"
                            : "최신 상태"}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span>마지막 저장</span>
                  <span className="font-semibold text-slate-900">{formatPersistedAt(lastPersistedAt)}</span>
                </div>
              </div>

              {isDirty ? (
                <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                  좌석 위치 또는 존 설정이 아직 저장되지 않았습니다.
                </div>
              ) : null}

              <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Canvas</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{selectedSeatIds.length}개 선택됨</p>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                    {activeTool === "select" ? "선택" : activeTool === "pan" ? "이동" : "줌"} 모드
                  </span>
                </div>
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  선택 모드에서는 클릭과 드래그 박스로 좌석을 고르고, 이동 모드에서는 캔버스를 끌어서 이동하고, 줌 모드에서는 클릭 또는 휠로 확대/축소합니다.
                </p>
              </div>

              <div className="mt-8 border-t border-slate-200 pt-6">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-900">요금 존</h3>
                  <button className="text-sm font-semibold text-blue-600" onClick={addTier}>
                    + 추가
                  </button>
                </div>

                <div className="space-y-3">
                  {draft.packages.map((item, index) => (
                    <div
                      key={item.id || item.client_key || index}
                      className={`rounded-xl border p-3 ${selectedTierIndex === index ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}
                      onClick={() => setSelectedTierIndex(index)}
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <input
                          className="w-full rounded-lg border border-slate-200 px-2 py-1 text-sm"
                          value={item.name}
                          onChange={(event) => {
                            const packages = [...draft.packages];
                            packages[index] = { ...packages[index], name: event.target.value };
                            setDraft({ ...draft, packages });
                          }}
                        />
                        {draft.packages.length > 1 ? (
                          <button className="text-xs font-semibold text-rose-500" onClick={() => removeTier(index)}>
                            삭제
                          </button>
                        ) : null}
                      </div>
                      <div>
                        <input
                          type="color"
                          className="h-10 w-full rounded-lg border border-slate-200"
                          value={item.color}
                          onChange={(event) => {
                            const packages = [...draft.packages];
                            packages[index] = { ...packages[index], color: event.target.value };
                            setDraft({ ...draft, packages });
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="min-w-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-[#f7f7fb] p-4 shadow-sm">
          {draft ? (
            <div className="flex h-full min-h-0 gap-4">
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-3">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{selectedPackage?.name}</h3>
                    <p className="text-sm text-slate-500">상단 툴바에서 모드를 바꾸고, 캔버스 안에서 이동과 줌을 조정하세요.</p>
                  </div>
                  {layoutQuery.isFetching ? <span className="text-xs text-slate-400">동기화 중...</span> : null}
                </div>

                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  <span>선택 좌석 {selectedSeatIds.length}개</span>
                  <span>캔버스 {draft.canvas_width} x {draft.canvas_height}</span>
                  <span>표시 배율 {(canvasScale * 100).toFixed(0)}%</span>
                </div>

                <div ref={canvasViewportRef} className="relative h-full min-h-0 flex-1 overflow-hidden rounded-2xl bg-[#FFF7E8]">
                  <div className="h-full w-full overflow-hidden rounded-2xl" style={{ cursor: canvasCursor }}>
                    <Stage
                      ref={stageRef}
                      width={stageWidth}
                      height={stageHeight}
                      onMouseDown={handleStageMouseDown}
                      onMouseMove={handleStageMouseMove}
                      onMouseUp={handleStageMouseUp}
                      onTouchStart={handleStageMouseDown}
                      onTouchMove={handleStageMouseMove}
                      onTouchEnd={handleStageMouseUp}
                      onWheel={handleStageWheel}
                      style={{ borderRadius: 16, background: "#FFF7E8" }}
                    >
                      <Layer>
                        <Group x={canvasOrigin.x} y={canvasOrigin.y} scaleX={canvasScale} scaleY={canvasScale}>
                          <Rect x={0} y={0} width={draft.canvas_width} height={draft.canvas_height} fill="#FFFDF7" cornerRadius={24} name="canvas-background" />
                          {draft.background_image_url ? (
                            <BackgroundImage
                              backgroundImageUrl={draft.background_image_url}
                              width={draft.canvas_width}
                              height={draft.canvas_height}
                            />
                          ) : null}
                          {draft.seats.map((seat) => {
                            const seatColor = findTierColor(draft, seat);
                            const isSelected = selectedSeatIdsSet.has(seat.spot);
                            const renderedSeatPosition = getRenderedSeatPosition(seat);
                            const seatShape = getSeatButtonShape(seat.meta);
                            const seatGeometry = seatShape ? getSeatShapeGeometry(seatShape, seat.width, seat.height) : null;
                            const seatFill = seat.is_active ? seatColor : "#CBD5E1";
                            const seatStroke = isSelected ? "#0F172A" : "#FFFFFF";
                            const seatStrokeWidth = isSelected ? 4 : 2;
                            const textOffsetY = getSeatShapeTextOffset(seatShape, seat.height);
                            return (
                              <Group
                                key={seat.id || seat.spot}
                                x={renderedSeatPosition.x}
                                y={renderedSeatPosition.y}
                                rotation={seat.rotation}
                                draggable={activeTool === "select"}
                                listening={activeTool === "select"}
                                onMouseDown={(event) => handleSeatPointerDown(seat, event)}
                                onTouchStart={(event) => handleSeatPointerDown(seat, event)}
                                onDragStart={() => handleSeatDragStart(seat)}
                                onDragMove={(event) => handleSeatDragMove(seat, event)}
                                onDragEnd={(event) => handleSeatDragEnd(seat, event)}
                                onDragCancel={handleSeatDragCancel}
                                onClick={(event) => handleSeatSelect(seat.spot, event)}
                                onTap={(event) => handleSeatSelect(seat.spot, event)}
                              >
                                {seatGeometry?.kind === "polygon" ? (
                                  <Line
                                    points={seatGeometry.points}
                                    closed
                                    fill={seatFill}
                                    shadowBlur={isSelected ? 18 : 8}
                                    shadowOpacity={0.18}
                                    stroke={seatStroke}
                                    strokeWidth={seatStrokeWidth}
                                  />
                                ) : (
                                  <Rect
                                    width={seat.width}
                                    height={seat.height}
                                    fill={seatFill}
                                    cornerRadius={seatShape ? 2 : 18}
                                    shadowBlur={isSelected ? 18 : 8}
                                    shadowOpacity={0.18}
                                    stroke={seatStroke}
                                    strokeWidth={seatStrokeWidth}
                                  />
                                )}
                                <Text
                                  width={seat.width}
                                  height={seat.height}
                                  y={textOffsetY}
                                  text={seat.label || seat.spot_name || String(seat.spot)}
                                  fontSize={14}
                                  fontStyle="bold"
                                  fill="#FFFFFF"
                                  align="center"
                                  verticalAlign="middle"
                                />
                              </Group>
                            );
                          })}
                          {selectionRect?.visible ? (
                            <Rect
                              {...normalizeSelectionRect(selectionRect)}
                              fill="rgba(37, 99, 235, 0.12)"
                              stroke="#2563EB"
                              strokeWidth={2}
                              dash={[10, 6]}
                              listening={false}
                            />
                          ) : null}
                        </Group>
                      </Layer>
                    </Stage>
                  </div>
                </div>
              </div>

              <div className="h-full w-[320px] shrink-0 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4">
                {selectedSeat ? (
                  <>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Seat Inspector</p>
                    <h3 className="mt-2 text-xl font-semibold text-slate-900">{selectedSeat.label || selectedSeat.spot_name}</h3>

                    <div className="mt-5 space-y-4">
                      <div>
                        <label className="mb-2 block text-xs font-semibold text-slate-500">라벨</label>
                        <input
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          value={selectedSeat.label}
                          onChange={(event) => updateSeat(selectedSeat.spot, { label: event.target.value })}
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold text-slate-500">요금 존</label>
                        <select
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          value={selectedSeat.layout_package || selectedSeat.layout_package_client_key || ""}
                          onChange={(event) => {
                            const matchedTier = draft.packages.find(
                              (item) => String(item.id || item.client_key) === event.target.value,
                            );
                            updateSeat(selectedSeat.spot, {
                              layout_package: matchedTier?.id,
                              layout_package_client_key: matchedTier?.id ? undefined : matchedTier?.client_key,
                            });
                          }}
                        >
                          {draft.packages.map((item) => (
                            <option key={item.id || item.client_key} value={String(item.id || item.client_key)}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold text-slate-500">좌석 모양</label>
                        <select
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          value={getSeatButtonShape(selectedSeat.meta) || ""}
                          onChange={(event) => updateSeat(selectedSeat.spot, {
                            meta: withSeatButtonShapeMeta(selectedSeat.meta, event.target.value as SeatButtonShape),
                          })}
                        >
                          <option value="" disabled>
                            모양 선택
                          </option>
                          {SEAT_BUTTON_SHAPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="mb-2 block text-xs font-semibold text-slate-500">Width</label>
                          <input
                            type="number"
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            value={selectedSeat.width}
                            onChange={(event) => updateSeat(selectedSeat.spot, { width: Number(event.target.value) || 1 })}
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-xs font-semibold text-slate-500">Height</label>
                          <input
                            type="number"
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            value={selectedSeat.height}
                            onChange={(event) => updateSeat(selectedSeat.spot, { height: Number(event.target.value) || 1 })}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="mb-2 block text-xs font-semibold text-slate-500">X</label>
                          <input
                            type="number"
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            value={Math.round(selectedSeat.x)}
                            onChange={(event) => updateSeat(selectedSeat.spot, { x: Number(event.target.value) || 0 })}
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-xs font-semibold text-slate-500">Y</label>
                          <input
                            type="number"
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            value={Math.round(selectedSeat.y)}
                            onChange={(event) => updateSeat(selectedSeat.spot, { y: Number(event.target.value) || 0 })}
                          />
                        </div>
                      </div>

                      <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-3 text-sm font-medium text-slate-700">
                        <input
                          type="checkbox"
                          checked={selectedSeat.is_active}
                          onChange={(event) => updateSeat(selectedSeat.spot, { is_active: event.target.checked })}
                        />
                        좌석 선택 활성화
                      </label>
                    </div>
                  </>
                ) : selectedSeats.length > 1 ? (
                  <>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Multi Selection</p>
                    <h3 className="mt-2 text-xl font-semibold text-slate-900">{selectedSeats.length}개 좌석 선택됨</h3>

                    <div className="mt-5 space-y-4">
                      <div>
                        <label className="mb-2 block text-xs font-semibold text-slate-500">요금 존 일괄 변경</label>
                        <select
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          defaultValue=""
                          onChange={(event) => applySelectedTier(event.target.value)}
                        >
                          <option value="" disabled>
                            존 선택
                          </option>
                          {draft.packages.map((item) => (
                            <option key={item.id || item.client_key} value={String(item.id || item.client_key)}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold text-slate-500">좌석 모양 일괄 변경</label>
                        <select
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          defaultValue=""
                          onChange={(event) => applySelectedShape(event.target.value)}
                        >
                          <option value="" disabled>
                            모양 선택
                          </option>
                          {SEAT_BUTTON_SHAPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-3 text-sm font-medium text-slate-700">
                        <input
                          type="checkbox"
                          checked={allSelectedSeatsActive}
                          onChange={(event) => updateSeats(selectedSeatIds, { is_active: event.target.checked })}
                        />
                        좌석 선택 활성화
                      </label>

                      {selectedSeatBounds ? (
                        <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                          <div className="flex items-center justify-between">
                            <span>선택 영역 X</span>
                            <span className="font-semibold text-slate-900">{Math.round(selectedSeatBounds.left)} - {Math.round(selectedSeatBounds.right)}</span>
                          </div>
                          <div className="mt-1 flex items-center justify-between">
                            <span>선택 영역 Y</span>
                            <span className="font-semibold text-slate-900">{Math.round(selectedSeatBounds.top)} - {Math.round(selectedSeatBounds.bottom)}</span>
                          </div>
                        </div>
                      ) : null}

                      <p className="text-sm leading-6 text-slate-500">
                        라벨과 개별 크기 편집은 단일 선택에서 제공됩니다. 여러 좌석은 정렬, 분배, 존 변경, 활성화 상태를 일괄 적용할 수 있습니다.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex h-full items-center justify-center text-center text-sm text-slate-400">
                    좌석을 선택하면 세부 설정이 열립니다.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-slate-400">레이아웃을 준비 중입니다.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeatLayoutStudio;
